import {ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnChanges, Output, ViewChild} from '@angular/core';
import {TagService} from '@webmate-web-core/shared/services/tag.service';
import {Popover} from 'primeng/popover';
import {ProjectId} from '@webmate-web-core/shared/types/ids';
import {ResourceType, Tag} from '@webmate-web-core/shared/types/tag';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {tap} from 'rxjs/operators';
import _ from "lodash";

@Component({
    selector: 'wm-tags',
    templateUrl: './tags.component.html',
    styleUrls: ['./tags.component.less']
})
export class TagsComponent implements OnChanges {

    constructor(private cdr: ChangeDetectorRef, private tagService: TagService, private notificationService: WmNotificationService) {
    }

    @ViewChild('tagPanel') tagPanel: Popover;

    @Input()
    projectId: ProjectId;

    @Input()
    resourceId: string;

    @Input()
    resourceType: ResourceType = 'test_execution'; // default resource type, can be overridden by the parent component

    @Input()
    size: 'small' | 'large' = 'large'; // size of the tag input field, can be small or large

    @Input()
    tags?: Array<Tag>;

    @Input()
    onlyread?: boolean = false;

    // if set, the tags will not be submitted to the server, but only stored in the component. This is useful when there is no resource
    // in the backend yet (e.g. with creation dialogs).
    @Input()
    onlyLocal?: boolean = false;

    // emits the new list of tags whenever it changes
    @Output()
    tagsChanged: EventEmitter<any> = new EventEmitter();

    newTagInsertionInProgress = false;

    theTags: Array<Tag>;

    newTagInput?: string = '';
    newTagPattern: RegExp = /^[a-zA-Z0-9_,()\\-]+[a-zA-Z0-9_,.()-]*=?[a-zA-Z0-9_,.()]*$/;

    tagInputDisabled = false;

    @HostBinding('class.tag-is-added')
    showingTagPanel = false;

    prettierTags: Array<[string, any]> = [];

    prettifyTags() {
        this.prettierTags = [];
        this.theTags.forEach(tag => {
            const name = Object.keys(tag)[0];
            this.prettierTags.push([name, tag[name]]);
        });
        this.prettierTags = this.prettierTags.toSorted((one, two) => one[0].localeCompare(two[0]));
    }

    ngOnChanges() {
        if (!!this.tags) { // prefilled
            this.theTags = [...this.tags];
            this.prettifyTags();
        } else {
            this.refresh();
        }
    }

    refresh() {
        if (this.onlyLocal) {
            if (!this.theTags) {
                this.theTags = [];
            }
            this.cdr.markForCheck();
            return;
        } else {
        this.tagService.getTagsForResource$(this.resourceId, true).subscribe(tags => {
                if (!this.onlyread && !_.isEqual(this.theTags, tags)) {
                    this.tagsChanged.emit(tags);
                }
            this.theTags = tags;
            this.prettifyTags();
            this.cdr.markForCheck();
        });
    }
    }

    deleteTag(prettierTag: string[]) {
        if (this.onlyLocal) {
            this.theTags = this.theTags.filter(t => {
                const [key, value] = Object.entries(t)[0];
                return !(prettierTag[0] === key && (prettierTag[1] === undefined || prettierTag[1] === value));
            });
            this.prettifyTags();
            this.tagsChanged.emit(this.theTags);
        } else {
            this.tagService.deleteTag$(this.resourceId, prettierTag[0] /* TODO: I think that's wrong with multiple equal keys */)
                .subscribe(_ => this.refresh());
        }
    }

    tagFromString(tagString: string): Tag {
        const tagRegex = /([^=]+)\s*=?\s*(\S+)?/g;
        const match = tagRegex.exec(tagString);
        if (match) {
            const name = match[1];
            if (!name) {
                return undefined;
            }
            const value = match?.[2];
            return {[name]: value || null}; // name-only tags are represented with null value.
        }
        return undefined;
    }

    validateTag() {
        const pattern = new RegExp(this.newTagPattern);
        return pattern.test(this.newTagInput || '');
    }

    hideTagOverlay() {
        this.newTagInput = '';
        this.showingTagPanel = false;
        this.newTagInsertionInProgress = false;
    }

    showTagOverlay() {
        this.showingTagPanel = true;
        // Awful code ahead. For some reason I couldn't figure out, the arrow of the overlaypanel is misaligned
        // In the regular mode, the arrow is just not centered under the plus image,
        // in case the position is flipped, i.e. the popup would reach out of the screen, the arrow is located besides the overlay
        //
        // the code below queries the position of the arrow and fixes it accordingly.
        // The if case, if the position is 177px, is the case in which the arrow is located besides the overlay.
        // We simply move it a few pixels back into the right position
        // The else case triggers in the normal state of the overlay. We move the arrow to a position where its aligned with the plus
        //
        // In case the content of the overlaypanel ever changes, this code probably needs to be adapted
        const arrowPos = this.tagPanel?.container?.style.getPropertyValue('--overlayArrowLeft');
        if (arrowPos && arrowPos === '177px') {
            this.tagPanel?.container?.style.setProperty('--overlayArrowLeft', '173px');
        } else {
            this.tagPanel?.container?.style.setProperty('--overlayArrowLeft', '-8px');
        }
    }

    addTag() {
        if (!this.validateTag()) {
            return;
        }
        if (this.onlyLocal) {
            const tag = this.tagFromString(this.newTagInput || '');
            this.theTags = [...this.theTags, tag];
            this.prettifyTags();
            this.clearNewTag();
            this.tagsChanged.emit(this.theTags);
        } else {
        this.newTagInsertionInProgress = true;

        this.tagService.addTagsForResource$(this.projectId, this.resourceType, this.resourceId, [this.newTagInput])
            .pipe(tap(() => {
                this.tagPanel.hide();
                this.clearNewTag();
                this.refresh();
            })).subscribe({
                error: (error) => {
                    this.notificationService.toastErrorNotification(error.message);
                }
            });
    }
    }

    clearNewTag() {
        this.newTagInput = undefined;
        this.showingTagPanel = false;
        // this.tagPanel.hide();
        this.newTagInsertionInProgress = false;
    }

    onEnterKeydown() {
        if (this.validateTag()) {
            this.tagInputDisabled = true;
            this.addTag();
        }
    }

    protected readonly JSON = JSON;
}
